<template>
  <div
    class="bg-primary fixed top-0 left-0 z-50 flex h-full w-full shrink-0 translate-x-full flex-col overflow-y-auto text-white transition-transform md:static md:w-60 md:translate-x-0 md:transition-none"
    :class="menuIsVisible ? 'translate-x-0!' : ''"
  >
    <!-- Clickable logo -->
    <div
      class="has-[a:hover]:bg-primary-400 flex items-center justify-between px-6"
    >
      <RouterLink :to="{ name: 'dashboard' }" class="cursor-pointer">
        <logoSvg class="h-16 w-48 fill-white" />
      </RouterLink>
      <BaseButton
        color="primary"
        minimal-dark
        square
        class="block md:hidden"
        @click="closeMenu"
      >
        <BaseIcon name="close" class="text-xl" />
      </BaseButton>
    </div>
    <!-- Menu items -->
    <nav>
      <ul>
        <li v-for="(item, index) in props.items" :key="index" class="group">
          <!-- Section header, dropped for sections without a name -->
          <div
            v-if="
              item.section &&
              (index === 0 || item.section !== props.items[index - 1].section)
            "
            class="text-primary-100 px-6 py-3 text-xs font-medium group-first:mt-2"
          >
            {{ item.section }}
          </div>
          <!-- Menu item -->
          <component
            :is="item.route ? 'router-link' : 'div'"
            :to="{ name: item.route }"
            class="hover:bg-primary-400 flex cursor-pointer items-center gap-2 px-6 py-3 font-medium whitespace-nowrap"
            :class="
              isItemActive(item)
                ? 'bg-primary-600 hover:bg-primary-600! cursor-default!'
                : ''
            "
            @click="item.action?.()"
          >
            <div class="relative flex items-center">
              <!-- Ping indicator -->
              <div
                v-if="item.ping"
                class="bg-danger absolute -top-1 -left-1 size-3 rounded-full"
              >
                <div
                  v-if="pingAnimate"
                  class="bg-danger size-full animate-ping rounded-full"
                />
              </div>
              <!-- Icon -->
              <BaseIcon
                v-if="item.icon"
                :name="item.icon"
                filled
                class="text-xl"
              />
            </div>
            <TextLabel class="truncate">
              <slot :name="item.name">{{ item.name }}</slot>
            </TextLabel>
          </component>
          <!-- Section divider -->
          <BaseDivider
            v-if="
              index === props.items.length - 1 ||
              item.section !== props.items[index + 1].section
            "
            class="border-primary-300! mt-2"
          />
        </li>
      </ul>
    </nav>
    <div class="grow" />
    <!-- User info -->
    <RouterLink
      v-if="sessionStore.isAuthenticated"
      :to="{ name: 'settings-account' }"
      class="hover:bg-primary-400 flex cursor-pointer items-center gap-2 px-6 py-3 font-medium whitespace-nowrap"
    >
      <BaseIcon name="user" filled class="text-xl" />
      <div v-if="userStore.fullName" class="w-full min-w-0">
        <TextLabel class="truncate">{{ userStore.fullName }}</TextLabel>
        <TextLabel class="truncate text-xs">{{ userStore.email }}</TextLabel>
      </div>
      <div v-else class="w-full min-w-0">
        <TextLabel class="truncate">{{ userStore.email }}</TextLabel>
      </div>
    </RouterLink>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, watch } from "vue"
import { useRoute } from "vue-router"
import useSessionStore from "@/stores/sessionStore.js"
import useUserStore from "@/stores/userStore.js"
import {
  BaseIcon,
  BaseDivider,
  BaseButton,
  TextLabel,
} from "@repowerednl/ui-component-library"

const props = defineProps({
  /**
   * Each item
   */
  items: {
    type: Array,
    required: true,
  },
})

const route = useRoute()
const sessionStore = useSessionStore()
const userStore = useUserStore()
const pingAnimate = ref(true)
let pingTimer = null

const logoSvg = computed(() => {
  return defineAsyncComponent(() => import(`/src/assets/repowered-logo.svg`))
})

/**
 * Show ping animation for 10 seconds when menu items change
 */
watch(
  () => props.items,
  () => {
    clearInterval(pingTimer)
    pingAnimate.value = true
    pingTimer = setInterval(() => {
      pingAnimate.value = false
    }, 10000)
  },
  { immediate: true },
)

/**
 * Menu visibility
 */
const menuIsVisible = ref(false)

function isItemActive(item) {
  if (!item?.route) {
    return false
  }

  return route.name === item.route || item.activeRoutes?.includes(route.name)
}

function openMenu() {
  menuIsVisible.value = true
}

function closeMenu() {
  menuIsVisible.value = false
}

watch(
  () => route.name,
  () => {
    if (menuIsVisible.value) {
      closeMenu()
    }
  },
)

defineExpose({
  openMenu,
  closeMenu,
})
</script>
